import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  Textarea,
  Container,
  Input,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { useOutletContext } from "react-router-dom"
import Navigation from '../Navigation/Navigation';

const ASSIGNMENTS = [
  "A retired clockmaker discovers that one of the clocks in his shop has started ticking backwards. Write the scene where he realizes what it means.",
  "Write a letter from a lighthouse keeper to their replacement, warning them about something that isn't in the official handbook.",
  "Two strangers are stuck in an elevator during a blackout. One of them is carrying something they shouldn't be. Write their conversation.",
  "A food critic visits a restaurant where every dish triggers a vivid childhood memory. Describe the meal.",
  "Write a monologue from the perspective of the last bookshop in a city that has gone fully digital.",
  "A detective arrives at a crime scene, but the only witness is a parrot. Write the interrogation.",
  "Describe a morning routine set 200 years in the future, but make it feel as mundane and relatable as brushing your teeth today.",
  "Write a scene where two old friends meet at a funeral and realize they remember the deceased as completely different people.",
  "A street musician plays their final performance before giving up music forever. Describe the scene from the perspective of someone in the crowd.",
  "Write a product recall notice for a teleportation device that has a very specific, very inconvenient glitch.",
  "A child writes a strongly worded complaint letter to the tooth fairy about unfair exchange rates.",
  "Describe a first date between two people who can both read minds, but don't know the other person can too.",
  "Write the opening paragraph of a thriller where the tension comes entirely from someone trying to parallel park.",
  "A travel blogger reviews a hotel that exists between dimensions. Write the 3-star review.",
  "Write a scene where a character has to explain to their grandparent what they do for a living, but their job didn't exist 20 years ago.",
  "An astronaut sends a voice message home after seeing something unexplainable outside the station window. Write the transcript.",
  "Describe the last 5 minutes of a chess match between two rivals who haven't spoken in 30 years.",
  "Write a diary entry from someone who just discovered they've been sleepwalking to the same location every night for a month.",
  "A museum security guard writes an incident report about a painting that changed overnight. Keep it professional.",
  "Write a scene set in a world where lying is physically impossible. A politician is giving a campaign speech.",
  "Two neighbors communicate only through passive-aggressive notes on the shared fridge. Write a week's worth of escalation.",
  "Describe a sunset on a planet with three suns, through the eyes of someone who has never seen just one.",
  "Write the inner monologue of a translator during a high-stakes diplomatic meeting where a single word could start a war.",
  "A baker discovers that their sourdough starter, which has been alive for 80 years, has started humming. Write what happens next.",
  "Write a persuasive essay from the perspective of a house cat arguing that they should be allowed outside.",
  "Describe the moment a time traveler realizes they accidentally invented the thing they came back to prevent.",
  "Write a scene where someone finds a handwritten note in a used book that seems to be addressed directly to them.",
  "A robot therapist has its first session with a human patient who doesn't know their therapist isn't human. Write the session.",
  "Write about a photographer who discovers that their old film camera captures photos of places one day in the future.",
  "Describe the awkward small talk at a dinner party where every guest secretly knows the host is an undercover spy.",
]

const DEFAULT_MINUTES = 5;

function generateAssignment() {
  return ASSIGNMENTS[Math.floor(Math.random() * ASSIGNMENTS.length)];
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function Practice() {
  const {
    state,
    onRouteChange,
    onLogOut,
    onGetPracticeFeedback,
    isAuthenticated
  } = useOutletContext();

  const [assignment, setAssignment] = useState(() => generateAssignment());
  const [text, setText] = useState('');
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const [timeLeft, setTimeLeft] = useState(DEFAULT_MINUTES * 60);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [extraInstructions, setExtraInstructions] = useState('');
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (timerRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            setTimerRunning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerRunning, timeLeft]);

  const handleTextChange = useCallback((e) => {
    setText(e.target.value);
    if (!timerStarted && e.target.value.length > 0) {
      setTimerStarted(true);
      setTimerRunning(true);
    }
  }, [timerStarted]);

  const handleNewAssignment = () => {
    setAssignment(generateAssignment());
    setText('');
    setTimeLeft(minutes * 60);
    setTimerRunning(false);
    setTimerStarted(false);
    setFeedback('');
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const handleMinutesChange = (valueStr) => {
    const val = parseInt(valueStr) || 1;
    setMinutes(val);
    if (!timerStarted) {
      setTimeLeft(val * 60);
    }
  };

  const handleGetFeedback = async () => {
    setLoadingFeedback(true);
    setFeedback('');
    const result = await onGetPracticeFeedback(assignment, text, extraInstructions);
    if (result) {
      setFeedback(result);
    }
    setLoadingFeedback(false);
  };

  const timerColor = timeLeft <= 60 ? '#E05252' : timeLeft <= 120 ? '#D97706' : '#52525B';

  return (
    <>
      <Navigation
        bg="#F5F3F0"
        state={state}
        onRouteChange={onRouteChange}
        onLogOut={onLogOut}
        isAuthenticated={isAuthenticated}
        p="10px"
      />
      <Flex
        minH={'94.6vh'}
        align={'flex-start'}
        justify={'center'}
        bg="#F5F3F0"
        pt={8}
      >
        <Container maxW="800px" py={6}>
          <Stack spacing={6}>
            <Stack align={'center'}>
              <Heading fontStyle="oblique" as="h1" fontSize="3em" color="#1A1A1A" fontWeight="700" letterSpacing="-0.02em">
                Practice
              </Heading>
            </Stack>

            <Box
              bg="white"
              rounded="xl"
              boxShadow="0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)"
              border="1px solid"
              borderColor="#E4E4E7"
              p={6}
            >
              <Stack spacing={4}>
                <Text fontSize="sm" color="#A1A1AA" fontWeight="700" textTransform="uppercase" letterSpacing="wide">
                  Your Assignment
                </Text>
                <Text fontSize="lg" fontWeight="500" color="#1A1A1A">
                  {assignment}
                </Text>
                <HStack spacing={4} align="center">
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor="#E05252"
                    color="#E05252"
                    borderRadius="lg"
                    _hover={{ bg: '#FEF2F2' }}
                    onClick={handleNewAssignment}
                  >
                    New Assignment
                  </Button>
                  <HStack spacing={2} align="center">
                    <Text fontSize="sm" color="#A1A1AA" whiteSpace="nowrap">Timer:</Text>
                    <NumberInput
                      size="sm"
                      w="80px"
                      min={1}
                      max={60}
                      value={minutes}
                      onChange={handleMinutesChange}
                      isDisabled={timerStarted}
                    >
                      <NumberInputField bg="white" borderColor="#E4E4E7" borderRadius="md" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text fontSize="sm" color="#A1A1AA">min</Text>
                  </HStack>
                </HStack>
              </Stack>
            </Box>

            <Flex justify="center">
              <Text fontSize="2xl" fontWeight="700" color={timerColor} fontVariantNumeric="tabular-nums">
                {formatTime(timeLeft)}
              </Text>
            </Flex>
            {!timerStarted && (
              <Text textAlign="center" fontSize="sm" color="#A1A1AA">
                Timer starts when you begin typing
              </Text>
            )}
            {timeLeft === 0 && (
              <Text textAlign="center" fontSize="sm" color="#E05252" fontWeight="600">
                Time's up!
              </Text>
            )}

            <Textarea
              placeholder="Start writing here..."
              value={text}
              onChange={handleTextChange}
              minH="300px"
              bg="white"
              fontSize="md"
              p={4}
              rounded="xl"
              border="1px solid"
              borderColor="#E4E4E7"
              boxShadow="0 1px 2px rgba(0,0,0,0.04)"
              resize="vertical"
              whiteSpace="pre-wrap"
              isDisabled={timeLeft === 0}
              _hover={{ borderColor: '#A1A1AA' }}
              _focus={{ borderColor: '#E05252', boxShadow: '0 0 0 1px #E05252' }}
            />

            <Input
              placeholder="Extra feedback instructions (optional), e.g. 'Focus on dialogue quality'"
              value={extraInstructions}
              onChange={(e) => setExtraInstructions(e.target.value)}
              bg="white"
              fontSize="sm"
              rounded="xl"
              border="1px solid"
              borderColor="#E4E4E7"
              boxShadow="0 1px 2px rgba(0,0,0,0.04)"
              _hover={{ borderColor: '#A1A1AA' }}
              _focus={{ borderColor: '#E05252', boxShadow: '0 0 0 1px #E05252' }}
            />

            <Button
              bg="#E05252"
              color="white"
              borderRadius="xl"
              fontWeight="600"
              _hover={{ bg: '#C94545' }}
              onClick={handleGetFeedback}
              isDisabled={text.trim().length === 0 || loadingFeedback}
              isLoading={loadingFeedback}
              loadingText="Getting Feedback..."
            >
              Get Feedback
            </Button>

            {feedback && (
              <Box
                bg="white"
                rounded="xl"
                boxShadow="0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)"
                border="1px solid"
                borderColor="#E4E4E7"
                p={6}
              >
                <Text fontSize="sm" color="#A1A1AA" fontWeight="700" textTransform="uppercase" letterSpacing="wide" mb={3}>
                  AI Feedback
                </Text>
                <Text whiteSpace="pre-wrap" color="#1A1A1A" fontSize="md" lineHeight="1.7">
                  {feedback}
                </Text>
              </Box>
            )}
          </Stack>
        </Container>
      </Flex>
    </>
  );
}

export default Practice;
